import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useClientQuery } from 'hooks/apollo';
import { Delete } from '@mui/icons-material';
import { TableCell, TableRow } from '@mui/material';
import { get, over } from '@totalsoft/change-tracking-react';
import { getErrors, isValid } from '@totalsoft/pure-validations-react';
import { Autocomplete, TextField } from '@totalsoft/rocket-ui';
import { PARTNER_HIERARCHY_DETAILS } from 'features/leasingIncomeSchema/queries';
import { valueOrDefault } from 'utils/functions';
import ActionsTableCell from 'components/common/table/ActionsTableCell';
import IconButton from 'components/common/buttons/IconButton';

const parentGroupSeparator = ' -> ';

const LeasingIncomeSchemaXGroupComponent = ({ lens, onDelete, validation, disabled }) => {
  const { t } = useTranslation();
  const clientQuery = useClientQuery();

  const loadPartnerHierarchyDetails = useCallback(
    async search => {
      if (!search) return [];
      const { data } = await clientQuery(PARTNER_HIERARCHY_DETAILS, {
        variables: { search }
      });

      const result = data?.partnerHierarchyDetails || [];
      const partnerHierarchyDetails = obj => {
        return {
          id: obj.partnerHierarchyDetailId,
          name: obj.category + parentGroupSeparator + obj.parentCategory
        };
      };

      return result?.map(partnerHierarchyDetails) || [];
    },
    [clientQuery]
  );

  const onChange = useCallback(
    lens => value => {
      over(lens, prev => {
        const next = { ...prev };
        if (value.id) next['groupId'] = value.id;
        if (value.name) {
          const values = value.name.split(parentGroupSeparator);
          next['groupName'] = values[0];
          next['parentGroupName'] = values[1];
        }
        return next;
      });
    },
    []
  );

  const groupName = { id: lens.groupId |> get, name: lens.groupName |> get };

  return (
    <TableRow>
      <TableCell>
        <Autocomplete
          value={groupName}
          loadOptions={loadPartnerHierarchyDetails}
          onChange={onChange(lens)}
          error={!isValid(validation.partnerId)}
          helperText={getErrors(validation.partnerId)}
          disabled={disabled}
        />
      </TableCell>
      <TableCell>
        <TextField fullWidth value={lens.parentGroupName |> get |> valueOrDefault('')} variant='standard' disabled />
      </TableCell>
      <ActionsTableCell width='100px'>
        <IconButton
          type='delete'
          color='error'
          size='tiny'
          variant='text'
          tooltip={t('Delete')}
          onClick={() => onDelete(lens.key |> get)}
          disabled={disabled}
        >
          <Delete />
        </IconButton>
      </ActionsTableCell>
    </TableRow>
  );
};

LeasingIncomeSchemaXGroupComponent.propTypes = {
  lens: PropTypes.object,
  onDelete: PropTypes.func,
  validation: PropTypes.object,
  disabled: PropTypes.bool
};

export default LeasingIncomeSchemaXGroupComponent;
